/**
 * S2 고민 글 · 내 일상 글 (시안 05 S2a~S2c · S1b-a, 결정 22~24, D46 · D47). 대화 말풍선이 아니라 **글 + 답글**.
 * 경로: /worry/:id(고민) · /board/posts/:id(상회칠판에서 올린 글). 일상 글이면 제목 「일상 글」 · 칩 「일상」.
 *
 *   상단 바(뒤로 · 「고민 글」 · ⋯ 고치기/지우기) → 글 → 답글(오래된 것이 위) → 입력창 「고민에 덧붙일 말」
 *
 * - 「답글」 → 그 멤버에게 대댓글. 그 멤버만 1.2~2.5초 뒤 같은 줄에 이어 답함. 한 줄에 멤버 답 3번까지
 * - 「{멤버}와 1:1로 이어 가기」 → 그 멤버 1:1(인용 상자 + 멤버가 먼저 말 검). 글에는 시스템 줄만 남음
 * - 첫 답(멤버 두 명)이 오는 동안과 멤버가 답글을 쓰는 동안은 1.5초마다 다시 읽음(90초까지)
 * - 위기면 안전 카드(접어 둘 수 있음). 리액션 없음
 * - 내 글 고치기: 답글은 그대로 「고침」 표시. 지우기: 글과 답글이 목록에서 사라짐(사용자 결정)
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MoreHorizontal } from 'lucide-react';
import { TopBar, IconButton } from '../../components/TopBar.jsx';
import { SafetyCard } from '../../components/SafetyCard.jsx';
import { BottomSheet } from '../../components/BottomSheet.jsx';
import { Button } from '../../components/Button.jsx';
import { MyMark, ReplyLine, ThreadComposer, ThreadPost } from '../../components/thread/Thread.jsx';
import { errorMessageOf, isAbortError } from '../../api/client.js';
import * as boardApi from '../../api/board.js';
import * as chatApi from '../../api/chat.js';
import { useGoBack } from '../../hooks/useGoBack.js';
import { useSession } from '../../stores/session.js';
import { formatClock } from '../board/boardFeed.js';

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 90 * 1000;
const SOURCE_LABEL = { room: '별채대화에서 올림', counter: '카운터에서 올림', board: '상회칠판에 올림' };

function isWaiting(thread) {
    return Boolean(thread && (thread.post.status === 'waiting' || thread.pending));
}

export function WorryPage() {
    const { worryId } = useParams();
    const navigate = useNavigate();
    const goBack = useGoBack('/');
    const displayName = useSession((state) => state.user?.displayName) ?? '단골';

    const [thread, setThread] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [target, setTarget] = useState(null);
    const [safetyCollapsed, setSafetyCollapsed] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const pollStartedAtRef = useRef(Date.now());

    const load = useCallback(
        async (signal) => {
            try {
                const data = await boardApi.fetchThread(worryId, { signal });
                setThread(data);
                setLoadError(null);
                return data;
            } catch (error) {
                if (!isAbortError(error)) {
                    setLoadError(errorMessageOf(error, '글을 불러오지 못했어요.'));
                }
                return null;
            }
        },
        [worryId]
    );

    useEffect(() => {
        const controller = new AbortController();
        pollStartedAtRef.current = Date.now();
        load(controller.signal);
        return () => controller.abort();
    }, [load]);

    // 첫 답 · 멤버 답글을 기다리는 동안 다시 읽습니다
    const waiting = isWaiting(thread);
    useEffect(() => {
        if (!waiting || Date.now() - pollStartedAtRef.current > POLL_TIMEOUT_MS) {
            return undefined;
        }
        const timer = setTimeout(() => load(), POLL_INTERVAL_MS);
        return () => clearTimeout(timer);
    }, [waiting, thread, load]);

    async function send(text) {
        setActionError(null);
        try {
            const data = await boardApi.postReply(worryId, { text, parentId: target?.repliesId ?? null });
            setThread(data);
            setTarget(null);
            pollStartedAtRef.current = Date.now();
        } catch (error) {
            setActionError(errorMessageOf(error, '답글을 보내지 못했어요.'));
            throw error;
        }
    }

    async function continueInChat(reply) {
        setActionError(null);
        try {
            const { memberKey, origin } = await chatApi.openChatFromReply(reply.memberKey, {
                postId: Number(worryId),
                replyId: reply.repliesId,
            });
            navigate(`/chat/${memberKey}`, { state: { from: 'reply', fromReply: { ...origin, memberKey } } });
        } catch (error) {
            setActionError(errorMessageOf(error, '1:1을 열지 못했어요.'));
        }
    }

    async function saveEdit() {
        setActionError(null);
        try {
            setThread(await boardApi.editPost(worryId, editing));
            setEditing(null);
        } catch (error) {
            setActionError(errorMessageOf(error, '글을 고치지 못했어요.'));
            if (error?.code === 'CRISIS_EDIT') {
                setEditing(null);
                setThread((previous) => (previous ? { ...previous, crisis: true } : previous));
            }
        }
    }

    async function removePost() {
        try {
            await boardApi.deletePost(worryId);
            setConfirmDelete(false);
            goBack();
        } catch (error) {
            setActionError(errorMessageOf(error, '글을 지우지 못했어요.'));
            setConfirmDelete(false);
        }
    }

    if (loadError && !thread) {
        return (
            <div className="flex min-h-dvh flex-col">
                <TopBar title="글" onBack={goBack} />
                <p role="alert" className="m-0 p-5 text-caption text-error">
                    {loadError}
                </p>
            </div>
        );
    }

    const post = thread?.post;
    const isDay = post?.kind === 'day';
    const pageTitle = isDay ? '일상 글' : '고민 글';
    return (
        <div className="flex h-dvh flex-col">
            <TopBar
                title={pageTitle}
                onBack={goBack}
                trailing={
                    post ? (
                        <IconButton label="글 메뉴" onClick={() => setMenuOpen(true)}>
                            <MoreHorizontal size={22} strokeWidth={1.6} aria-hidden="true" />
                        </IconButton>
                    ) : null
                }
            />
            <main className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                {post ? (
                    <>
                        <ThreadPost
                            mark={<MyMark name={displayName} size={36} />}
                            name={displayName}
                            meta={`${SOURCE_LABEL[post.source] ?? '올림'} · ${formatClock(post.createdAt)}`}
                            kind={post.kind}
                            body={post.body}
                            replyCount={thread.replyCount}
                            memberCount={thread.memberCount}
                            edited={Boolean(post.editedAt)}
                        />
                        {thread.crisis ? (
                            <div className="px-4 pt-3">
                                <SafetyCard
                                    level={post.safetyLevel === 'L3' ? 'L3' : 'L2'}
                                    collapsed={safetyCollapsed}
                                    onCollapse={() => setSafetyCollapsed(true)}
                                    onExpand={() => setSafetyCollapsed(false)}
                                />
                            </div>
                        ) : null}
                        {thread.lines.map((line) => (
                            <ReplyLine
                                key={line.repliesId}
                                line={line}
                                displayName={displayName}
                                activeTargetId={target?.repliesId ?? null}
                                pending={thread.pending}
                                onReply={(reply) => setTarget(target?.repliesId === reply.repliesId ? null : reply)}
                                onChat={continueInChat}
                            />
                        ))}
                        {post.status === 'waiting' && !thread.lines.length ? (
                            <p className="m-0 p-4 text-[13px] text-text-secondary">멤버들이 글을 읽는 중이에요.</p>
                        ) : null}
                        {thread.systemLines.map((line) => (
                            <p
                                key={line.repliesId}
                                className="m-0 px-4 py-3 text-center text-[12px] text-text-secondary"
                            >
                                {line.body}
                            </p>
                        ))}
                        {actionError ? (
                            <p role="alert" className="m-0 px-4 py-2 text-caption text-error">
                                {actionError}
                            </p>
                        ) : null}
                    </>
                ) : (
                    <p className="m-0 p-5 text-text-secondary">불러오는 중이에요.</p>
                )}
            </main>
            {post ? (
                <ThreadComposer
                    target={target}
                    placeholder={isDay ? '답글 남기기' : '고민에 덧붙일 말'}
                    disabled={Boolean(thread.pending)}
                    onClear={() => setTarget(null)}
                    onSend={send}
                />
            ) : null}

            <BottomSheet open={menuOpen} onClose={() => setMenuOpen(false)} label="글 메뉴">
                <div className="flex flex-col p-2 pb-6">
                    <button
                        type="button"
                        onClick={() => {
                            setMenuOpen(false);
                            setEditing(post.body);
                        }}
                        className="h-12 rounded-card px-4 text-left text-[15px]"
                    >
                        글 고치기
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setMenuOpen(false);
                            setConfirmDelete(true);
                        }}
                        className="h-12 rounded-card px-4 text-left text-[15px] text-error"
                    >
                        글 지우기
                    </button>
                </div>
            </BottomSheet>

            <BottomSheet open={editing !== null} onClose={() => setEditing(null)} label="글 고치기">
                <div className="flex flex-col gap-3 p-4 pb-6">
                    <h2 className="m-0 text-[17px] font-semibold">글 고치기</h2>
                    <textarea
                        value={editing ?? ''}
                        onChange={(event) => setEditing(event.target.value)}
                        maxLength={2000}
                        rows={7}
                        aria-label="고칠 글"
                        className="rounded-card border-[1.5px] border-brand bg-surface p-3 text-[16px] leading-[26px]"
                    />
                    <p className="m-0 text-[12px] text-text-secondary">고쳐도 멤버 답글은 그대로 남아요.</p>
                    <Button fullWidth disabled={!editing?.trim()} onClick={saveEdit}>
                        고친 글 올리기
                    </Button>
                </div>
            </BottomSheet>

            <BottomSheet open={confirmDelete} onClose={() => setConfirmDelete(false)} label="글 지우기">
                <div className="flex flex-col gap-3 p-4 pb-6">
                    <h2 className="m-0 text-[17px] font-semibold">이 글을 지울까요?</h2>
                    <p className="m-0 text-[14px] text-text-secondary">멤버 답글도 함께 사라져요.</p>
                    <Button fullWidth onClick={removePost} className="bg-error">
                        지우기
                    </Button>
                    <Button fullWidth variant="secondary" onClick={() => setConfirmDelete(false)}>
                        그대로 두기
                    </Button>
                </div>
            </BottomSheet>
        </div>
    );
}
